import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';
import { action, computed, makeObservable, observable, override } from 'mobx';

import { AnyObject, SortingOrder } from '@store/types';
import {
    EntryDetailData,
    EntryListItem as EntryListResponseParams,
    Optional,
} from 'chaika-frontend-types/frontend-cms';

import { SimpleList } from '@store/models/SimpleList';

export interface EntryFilterParams {
    ehrId: string;
    entryTypes: string[];
    authors: string[];
    organizations: string[];
    medicalCases: string[];
    statuses: string[];
}

export interface EntryListParams extends EntryListResponseParams {
    entryTypeType: string;
    entryTypeTitle: string;
    statusTitle?: string;
    indexNumber?: number;
}

const DEFAULT_FIELD = 'creationTime';

export class EntryList extends SimpleList<EntryListParams, EntryFilterParams> {
    protected readonly paginationStep: number = 30;
    @observable protected entryDetails = new Map<string, EntryDetailData>();
    @observable protected paginationLimit: number = this.paginationStep;
    @observable private medicalCaseIds = new Map<string, string[]>();

    constructor(params?: EntryListParams[]) {
        super(params);
        makeObservable(this);

        this.filterParams = {
            ehrId: '',
            entryTypes: [],
            authors: [],
            organizations: [],
            medicalCases: [],
            statuses: [],
        };

        this.orderParams = {
            fieldName: DEFAULT_FIELD,
            order: SortingOrder.DESC,
        };
    }

    @computed
    public get typeIds(): string[] {
        return uniq(this.values.map(it => it.typeId));
    }

    @computed
    public get hasMore(): boolean {
        return this.paginationLimit < this.filteredValues.length;
    }

    @computed
    public get paginateFilteredValues(): EntryListParams[] {
        return this.filteredValues.slice(0, this.paginationLimit);
    }

    @override
    public get filteredValues(): EntryListParams[] {
        const { fieldName = DEFAULT_FIELD, order = SortingOrder.DESC } = this.getOrderParams();

        const valuesWithIndexNumbers = this.getValuesWithIndexNumbers(this.values);
        const filteredValuesWithIndexNumbers = valuesWithIndexNumbers.filter(value =>
            this.filterValue(value, this.filterParams),
        );

        return orderBy(
            filteredValuesWithIndexNumbers,
            value => {
                if (fieldName === DEFAULT_FIELD) {
                    return value.visitTime ?? value.creationTime;
                }
                return (value as AnyObject)[fieldName];
            },
            order,
        );
    }

    public get hasEntryDetails(): boolean {
        return !!this.entryDetails?.size;
    }

    @action
    public showMoreItems(): void {
        if (this.paginationLimit < this.getSize()) {
            this.paginationLimit += this.paginationStep;
        }
    }

    @action
    public showAllItems(): void {
        this.paginationLimit = this.getSize();
    }

    @override
    public setFilterParams(params: Partial<EntryFilterParams> = {}): void {
        this.filterParams = { ...this.filterParams, ...params };
    }

    @action
    public setEntryDetails(details: EntryDetailData[]): void {
        this.entryDetails = new Map(details.map(detail => [detail.entryId, detail]));
    }

    @action
    public setMedicalCaseIds(entryId: string, medicalCaseIds: string[]): void {
        this.medicalCaseIds.set(entryId, medicalCaseIds);
    }

    public hasNotEmptyMedicalCaseIds(entryId: string): boolean {
        return !!this.medicalCaseIds.get(entryId)?.length;
    }

    public getEntryDetails(entryId: string): Optional<EntryDetailData> {
        return this.entryDetails.get(entryId);
    }

    protected getValuesWithIndexNumbers(values: EntryListParams[]): EntryListParams[] {
        return orderBy(values, value => value.visitTime ?? value.creationTime, SortingOrder.DESC).map(
            (value, index) => ({
                ...value,
                indexNumber: index + 1,
            }),
        );
    }

    protected filterValue(
        value: EntryListParams,
        { ehrId, entryTypes, organizations, authors, medicalCases, statuses }: EntryFilterParams,
    ): boolean {
        return (
            (ehrId === '' || value.ehrId === ehrId) &&
            (organizations.length === 0 || organizations.includes(value.organizationId)) &&
            (entryTypes.length === 0 || (!!value.entryTypeType && entryTypes.includes(value.entryTypeType))) &&
            (statuses.length === 0 || statuses.includes(value.status)) &&
            (authors.length === 0 ||
                authors.includes(value.authorId) ||
                (value.coAuthorIds ? value.coAuthorIds.some(id => authors.includes(id)) : false)) &&
            (medicalCases.length === 0 || medicalCases.every(id => this.medicalCaseIds.get(value.id)?.includes(id)))
        );
    }
}
